// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Project } from '@/project/api/types';
import { ProjectChart } from '@/report/components/project-chart';
import { useProjectReportChart } from '@/report/hooks/use-project-report-chart';
import { colors } from '@mui/material';

const countFormatter = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 0,
});

interface Props {
  width: number;
  height: number;
  projects: Project[];
  groupCount: number;
  emptyState: string;
  onHover: (id: string | null, color: string | null) => void;
}

export function ProjectRequestedAssetCountChart(props: Props) {
  const { data, total, isEmpty, getValue, getColor } = useProjectReportChart(
    props.projects,
    {
      count: props.groupCount,
      metric: 'requestedCount',
      colors: [colors.indigo[800], colors.indigo[100]],
    }
  );
  const isChartEmpty = isEmpty || props.projects.length === 0;
  const emptyState =
    isEmpty && props.projects.length > 0
      ? 'No assets planned for the upcoming projects'
      : props.emptyState;

  const handleHover = (id: string | null) => {
    if (id === null) {
      return props.onHover(null, null);
    }

    const index = data.findIndex((datum) => datum.id === id);
    const color = getColor(id, index);

    props.onHover(id, color);
  };

  return (
    <ProjectChart
      data={data}
      width={props.width}
      height={props.height}
      onHover={handleHover}
      isEmpty={isChartEmpty}
      emptyState={emptyState}
      getValue={getValue}
      getColor={getColor}
    >
      {countFormatter.format(total)}
    </ProjectChart>
  );
}
